/**
 * frontend/dashboards/CollectorDashboard.jsx
 * SoundDAO — Collector (Investor) dashboard — Milestone 5
 *
 * Live data from Supabase via backend API + real-time balance subscription.
 *
 * Sections:
 *   1. Balance card (live Supabase subscription)
 *   2. Portfolio summary — FReN count + total SOUND earned, broken down by creator
 *   3. Watchlist — search creators by email/wallet, add/remove, stored in watchlist table
 *   4. Atlas signal card — opens Atlas chat with pre-fill
 *
 * Props:
 *   user       — users row
 *   balance    — initial SOUND balance
 *   session    — Supabase auth session
 *   onSettings — navigate to /settings
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useNavigate } from 'react-router-dom';
import Chatbot from '../Chatbot';
import MissionCard from '../components/MissionCard';
import { getDisplayName } from '../utils/displayName';
import './Dashboard.css';
import { authFetch } from '../lib/authFetch';
import BrandMark from '../components/BrandMark';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// ── CollectorDashboard ─────────────────────────────────────────────────────────
export default function CollectorDashboard({ user, balance: initialBalance, session, onSettings }) {
  const user_id  = session?.user?.id || user?.id;
  const navigate = useNavigate();

  const [balance,      setBalance]      = useState(initialBalance || 0);
  const [weeklySound,  setWeeklySound]  = useState(0);
  const [portfolio,    setPortfolio]    = useState([]); // [{ creator_id, creator_email, fren_count, sound_earned }]
  const [totalEarned,  setTotalEarned]  = useState(0);
  const [watchlist,    setWatchlist]    = useState([]); // [{ id, creator_id, artist_name, stage, heat_tier }]
  const [searchQuery,  setSearchQuery]  = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching,    setSearching]    = useState(false);
  const [loadingData,  setLoadingData]  = useState(true);
  const [watchlistMsg, setWatchlistMsg] = useState('');

  const chatRef    = useRef(null);
  const searchRef  = useRef(null);

  // ── Load portfolio + watchlist ────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!user_id) return;
    setLoadingData(true);
    try {
      // Balance
      const balRes = await authFetch(`${API_BASE}/api/rewards/balance/${user_id}`);
      if (balRes.ok) {
        const d = await balRes.json();
        setBalance(d.sound_balance || 0);
        setTotalEarned(d.total_earned || 0);
      }

      // Weekly SOUND
      const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data: weekData } = await supabase
        .from('rewards').select('amount').eq('user_id', user_id).gte('created_at', since);
      setWeeklySound((weekData || []).reduce((s, r) => s + Number(r.amount), 0));

      // Portfolio — FReNs this collector holds (via fren_purchase rewards grouped by creator context)
      // We query fren_purchase rewards and group by metadata.creator_id
      const { data: frenRewards } = await supabase
        .from('rewards')
        .select('amount, metadata')
        .eq('user_id', user_id)
        .eq('action_type', 'fren_purchase');

      // Build portfolio map
      const portfolioMap = {};
      (frenRewards || []).forEach(r => {
        const creatorId = r.metadata?.creator_id || 'unknown';
        const creatorEmail = r.metadata?.creator_email || creatorId;
        if (!portfolioMap[creatorId]) {
          portfolioMap[creatorId] = { creator_id: creatorId, creator_email: creatorEmail, fren_count: 0, sound_earned: 0 };
        }
        portfolioMap[creatorId].fren_count += 1;
        portfolioMap[creatorId].sound_earned += Number(r.amount);
      });

      const portfolioList = Object.values(portfolioMap).sort((a, b) => b.sound_earned - a.sound_earned);
      setPortfolio(portfolioList);

      // Watchlist
      const wlRes = await authFetch(`${API_BASE}/api/watchlist/${user_id}`);
      if (wlRes.ok) {
        const wlData = await wlRes.json();
        setWatchlist(wlData.watchlist || []);
      }
    } catch (err) {
      console.error('[CollectorDashboard] loadData error:', err);
    } finally {
      setLoadingData(false);
    }
  }, [user_id]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Real-time balance ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user_id) return;
    const channel = supabase
      .channel(`collector-balances-${user_id}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'balances', filter: `user_id=eq.${user_id}` },
        payload => setBalance(Number(payload.new?.sound_balance || 0))
      )
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [user_id]);

  // ── Creator search ────────────────────────────────────────────────────────────
  const handleSearch = useCallback(async (q) => {
    setSearchQuery(q);
    if (!q || q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await authFetch(
        `${API_BASE}/api/watchlist/search?q=${encodeURIComponent(q)}`
      );
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.creators || []);
      }
    } catch (err) {
      console.error('[CollectorDashboard] search error:', err);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchQuery.length >= 2) handleSearch(searchQuery);
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, handleSearch]);

  // ── Add to watchlist ──────────────────────────────────────────────────────────
  const handleAddToWatchlist = async (creator) => {
    try {
      const res = await authFetch(`${API_BASE}/api/watchlist/add`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ creator_id: creator.creator_id }),
      });
      if (res.ok) {
        setWatchlist(prev => [...prev, { id: Date.now(), ...creator }]);
        setSearchResults(prev => prev.filter(c => c.creator_id !== creator.creator_id));
        setWatchlistMsg(`Added ${creator.artist_name} to watchlist`);
        setTimeout(() => setWatchlistMsg(''), 2500);
      }
    } catch (err) {
      console.error('[CollectorDashboard] add watchlist error:', err);
    }
  };

  // ── Remove from watchlist ─────────────────────────────────────────────────────
  const handleRemoveFromWatchlist = async (watchlistId, creatorId) => {
    try {
      const res = await authFetch(`${API_BASE}/api/watchlist/remove`, {
        method:  'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ creator_id: creatorId }),
      });
      if (res.ok) {
        setWatchlist(prev => prev.filter(w => w.creator_id !== creatorId));
      }
    } catch (err) {
      console.error('[CollectorDashboard] remove watchlist error:', err);
    }
  };

  // ── Open Atlas chat with pre-fill ─────────────────────────────────────────────
  const handleAtlasCTA = (msg) => {
    chatRef.current?.openWithMessage(
      msg || 'What creators should I be watching right now? Give me your top signals.'
    );
  };

  const watchlistIds = new Set(watchlist.map(w => w.creator_id));

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="dashboard dashboard--collector">

      {/* Header */}
      <header className="dashboard-header">
        <div className="dashboard-brand"><BrandMark /> SoundDAO <span className="version-badge">Beta v1.0</span></div>
        <div className="dashboard-header-actions">
          <a
            href="https://soundcoin.co/whitepaper"
            target="_blank"
            rel="noopener noreferrer"
            className="dashboard-header-link"
            title="Read the White Paper"
          >
            📄 White Paper
          </a>
          <button
            className="dashboard-header-link"
            onClick={() => navigate('/leaderboard')}
            title="Global Leaderboard"
          >
            🏆 Leaderboard
          </button>
          <button className="dashboard-settings-btn" onClick={onSettings} title="Settings">⚙️</button>
        </div>
      </header>

      {/* Hero */}
      <div className="dashboard-hero">
        <div className="dashboard-hero-emoji">💎</div>
        <h1 className="dashboard-hero-title">
          {getDisplayName(user)}
        </h1>
        {/* Sprint 6-Zero M2: collectors are not on the player ladder. Show the
            persona and portfolio size instead of a borrowed player rank. */}
        <p className="dashboard-hero-rank">
          Collector · {watchlist.length} on watchlist
        </p>
      </div>

      {/* Balance card */}
      <div className="dashboard-balance">
        <div className="dashboard-balance-amount">
          {loadingData ? '—' : balance.toLocaleString()}
        </div>
        <div className="dashboard-balance-label">
          SOUND Balance
          <span className="sound-tooltip" tabIndex={0}>
            ⓘ
            <span className="sound-tooltip-text">
              SOUND has a fixed supply of 2.1 billion. Points you earn convert to SOUND under a daily on-chain budget — at most 1 SOUND per point.
            </span>
          </span>
        </div>
        <p className="dashboard-balance-note">
          +{weeklySound.toLocaleString()} this week · {totalEarned.toLocaleString()} total earned
        </p>
      </div>

      {/* Portfolio summary */}
      <div className="dashboard-section">
        <h2 className="dashboard-section-title">Front Row (coming soon)</h2>

        <div className="dashboard-portfolio-summary">
          <div className="portfolio-stat">
            <span className="portfolio-stat-value">
              {loadingData ? '—' : portfolio.length}
            </span>
            <span className="portfolio-stat-label">Creators</span>
          </div>
          <div className="portfolio-stat-divider" />
          <div className="portfolio-stat">
            <span className="portfolio-stat-value">
              {loadingData ? '—' : totalEarned.toLocaleString()}
            </span>
            <span className="portfolio-stat-label">Total Earned</span>
          </div>
        </div>

        {loadingData ? (
          <div className="dashboard-section-loading">Loading portfolio…</div>
        ) : portfolio.length === 0 ? (
          <div className="dashboard-mission-card">
            <div className="dashboard-mission-icon">🎯</div>
            <div className="dashboard-mission-body">
              <p className="dashboard-mission-title">Start Your Portfolio</p>
              <p className="dashboard-mission-desc">
                FReN — Front Row collectibles for fans — coming soon.
              </p>
              <span className="fren-coming-soon">🔜 Coming Soon</span>
            </div>
          </div>
        ) : (
          <div className="dashboard-portfolio-list">
            {portfolio.map(item => (
              <div key={item.creator_id} className="portfolio-row">
                <div className="portfolio-creator-avatar">
                  {(item.creator_email || '?')[0].toUpperCase()}
                </div>
                <div className="portfolio-creator-info">
                  <p className="portfolio-creator-name">
                    {getDisplayName({ display_name: item.creator_display_name, email: item.creator_email }) }
                  </p>
                  <p className="portfolio-creator-meta">
                    {item.fren_count} FReN{item.fren_count !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="portfolio-creator-earned">
                  +{item.sound_earned.toLocaleString()} SOUND
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Atlas signal card */}
      <div
        className="dashboard-atlas-card"
        onClick={() => handleAtlasCTA()}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && handleAtlasCTA()}
      >
        <div className="atlas-card-header">
          <span className="atlas-card-icon">💎</span>
          <span className="atlas-card-title">Atlas · Portfolio Analyst</span>
          <span className="atlas-card-cta">Ask Atlas →</span>
        </div>
        <p className="atlas-card-desc">
          Get data-driven signals on creator momentum, Heat and Governor timing.
          Your edge in spotting who is next.
        </p>
      </div>

      {/* Watchlist */}
      <div className="dashboard-section">
        <h2 className="dashboard-section-title">Creator Watchlist</h2>

        {/* Search */}
        <div className="watchlist-search-wrap">
          <input
            ref={searchRef}
            className="watchlist-search"
            type="text"
            placeholder="Search creators by email or wallet…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searching && <span className="watchlist-searching">Searching…</span>}
        </div>

        {/* Search results */}
        {searchResults.length > 0 && (
          <div className="watchlist-results">
            {searchResults.map(creator => (
              <div key={creator.creator_id} className="watchlist-result-row">
                <div className="roster-avatar">
                  {(creator.artist_name || '?')[0].toUpperCase()}
                </div>
                <div className="roster-info">
                  <p className="roster-name">{creator.artist_name}</p>
                  <p className="roster-rank">{creator.stage} · {creator.heat_tier}</p>
                </div>
                <button
                  className="watchlist-add-btn"
                  onClick={() => handleAddToWatchlist(creator)}
                  disabled={watchlistIds.has(creator.creator_id)}
                >
                  {watchlistIds.has(creator.creator_id) ? 'Watching' : '+ Watch'}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Toast message */}
        {watchlistMsg && (
          <div className="watchlist-toast">{watchlistMsg}</div>
        )}

        {/* Watchlist items */}
        {loadingData ? (
          <div className="dashboard-section-loading">Loading watchlist…</div>
        ) : watchlist.length === 0 && !searchQuery ? (
          <div className="dashboard-empty-state">
            <p>No creators on your watchlist yet. Search above to add some.</p>
          </div>
        ) : (
          <div className="watchlist-items">
            {watchlist.map(item => (
              <div key={item.creator_id} className="watchlist-item">
                <div className="roster-avatar">
                  {(item.artist_name || '?')[0].toUpperCase()}
                </div>
                <div className="roster-info">
                  <p className="roster-name">{item.artist_name}</p>
                  {item.stage && <p className="roster-rank">{item.stage} · {item.heat_tier}</p>}
                </div>
                <button
                  className="watchlist-remove-btn"
                  onClick={() => handleRemoveFromWatchlist(item.id, item.creator_id)}
                  title="Remove from watchlist"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI Missions (Task 2.7) */}
      <div className="dashboard-section">
        <MissionCard userId={user_id} persona="collector" />
      </div>

      {/* Chatbot */}
      <Chatbot ref={chatRef} user={user} session={session} />
    </div>
  );
}
